import polygonClipping from 'polygon-clipping';

import type {
  GenerationCustomComponent,
  GenerationImpactResult,
  GenerationLayoutConfiguration,
  GenerationLayoutExclusionSet,
  GenerationLayoutExclusionZone,
  GenerationProjectInstalledReservations,
} from '../../types/generationImpact';
import type { SiteBoundaryParcel } from '../../types/siteBoundary';
import type { SiteLayer } from '../../types/site';
import {
  buildGenerationTestFit,
  rotatedClosedPolygon,
  SQUARE_METRES_PER_ACRE,
  type GenerationTestFitRectangle,
  type GenerationTestFitResult,
} from './generationTestFit';
import {
  closedRingM,
  localExclusionGeometry,
  multiPolygonAreaM2,
  polygonOverlapAcres,
  type LocalExclusionGeometry,
} from './generationLayoutUsableArea';
import {
  searchPlacementOffset,
  translateRectangle,
  type PlacementOffsetM,
} from './generationLayoutPlacementSearch';

const EARTH_RADIUS_M = 6_378_137;
const MIN_COS_LATITUDE = 1e-8;
const CONTAINMENT_TOLERANCE_ACRES = 0.01;
const CLEARANCE_TOLERANCE_M = 1e-6;
const SEGMENT_INTERSECTION_RELATIVE_TOLERANCE = 1e-12;
const MIN_CUSTOM_DIMENSION_M = 0.01;
const MAX_CUSTOM_DIMENSION_M = 1_000_000;
const MAX_CUSTOM_ROTATION_DEG = 360;

export const DEFAULT_GENERATION_LAYOUT: Readonly<GenerationLayoutConfiguration> = Object.freeze({
  rotationDeg: 0,
  orientation: 'auto',
  edgeSetbackM: 15.24, // 50 ft
  componentGapM: 30.48, // 100 ft
  generationBlockAspectRatio: 1.8,
});

export interface GenerationLayoutLocalPoint {
  xM: number;
  yM: number;
}

export interface GenerationLayoutEnvelope {
  center: { lat: number; lng: number };
  widthM: number;
  heightM: number;
  /** Parcel polygon in the same rotated, parcel-centred coordinates as the plan. */
  parcelPolygonM: readonly GenerationLayoutLocalPoint[];
}

export interface GenerationLayoutComponentPolygon {
  rectangle: GenerationTestFitRectangle;
  coordinates: Array<{ lat: number; lng: number }>;
  containedAcres: number | null;
  outsideAcres: number | null;
  /** Shortest local-plan distance from the component perimeter to the selected parcel ring. */
  parcelEdgeClearanceM: number | null;
  /** Null when no parcel is available; otherwise compares clearance with layout.edgeSetbackM. */
  meetsParcelEdgeSetback: boolean | null;
  /** Present only when exclusion zones are applied; null when overlap could
   * not be computed (clipping failure — treated as not-fitting). */
  exclusionOverlapAcres?: number | null;
  /** Studio (schemaVersion 2) only, present when pinned or custom components
   * exist: this component's overlap with OTHER fixed (pinned/custom)
   * geometry. Reported, never auto-resolved; null = analysis failed (treated
   * as not-fitting). */
  obstacleOverlapAcres?: number | null;
}

/** Buildable-area summary present only when exclusion zones are applied. */
export interface GenerationLayoutUsableSummary {
  parcelAcres: number;
  excludedAcres: number;
  usableAcres: number;
  femaStatus: GenerationLayoutExclusionSet['femaStatus'];
}

export interface GenerationParcelTestFitResult {
  status: 'fits' | 'partial' | 'indeterminate';
  plan: GenerationTestFitResult | null;
  layout: GenerationLayoutConfiguration;
  parcel: SiteBoundaryParcel | null;
  /** All selected site/study-area parcels so multi-parcel exports preserve every parcel and hole. */
  parcels?: readonly SiteBoundaryParcel[];
  envelope: GenerationLayoutEnvelope | null;
  anchor: { lat: number; lng: number };
  components: readonly GenerationLayoutComponentPolygon[];
  containedAcres: number | null;
  outsideAcres: number | null;
  containmentPct: number | null;
  /** Minimum component-to-parcel-boundary clearance across the physical layout. */
  minimumParcelEdgeClearanceM: number | null;
  /** Null when no parcel is available; false keeps the overall status from reporting `fits`. */
  parcelEdgeSetbackSatisfied: boolean | null;
  /** Present only when exclusion zones are applied and localizable. */
  usable?: GenerationLayoutUsableSummary;
  /** Total component overlap with exclusion zones. Present only when zones are
   * applied; null when the overlap could not be computed (fail closed). */
  exclusionOverlapAcres?: number | null;
  /** Studio (schemaVersion 2) only, present when pinned or custom components
   * exist: sum of per-component overlaps with other fixed geometry (pairwise
   * collisions are counted from both sides — a screening flag, not a
   * conservation quantity). Null = analysis failed (fail closed). */
  obstacleOverlapAcres?: number | null;
  /** Anchor-sweep translation applied to the packed group (pinned and custom
   * geometry never translates). Present only when exclusion zones or fixed
   * studio obstacles are applied. */
  placementOffsetM?: PlacementOffsetM;
  error: string | null;
  caveats: readonly string[];
}

export interface BuildGenerationParcelTestFitInput {
  parcels: readonly SiteBoundaryParcel[];
  layout?: GenerationLayoutConfiguration;
  fallbackAnchor: { lat: number; lng: number };
  fallbackEnvelopeAcres?: number;
  generationBlockCount: number;
  footprint: GenerationImpactResult['footprint'];
  installedReservations?: GenerationProjectInstalledReservations;
}

/** Merge one server-published scenario into a complete in-memory layer set. */
export function mergePublishedGenerationLayers(
  existing: readonly SiteLayer[],
  published: readonly SiteLayer[],
  scenarioId: string,
): SiteLayer[] {
  return [
    ...existing.filter(layer => !(
      layer.source === 'scenario-derived'
      && layer.attributes?.scenarioId === scenarioId
    )),
    ...published,
  ];
}

function normalizedLayout(
  layout: GenerationLayoutConfiguration | undefined,
  parcels: readonly SiteBoundaryParcel[],
): GenerationLayoutConfiguration {
  const preferred = parcels.find(parcel => parcel.intendedUse === 'datacenter')
    ?? [...parcels].sort((left, right) => right.area - left.area)[0];
  return {
    ...DEFAULT_GENERATION_LAYOUT,
    ...(preferred ? { targetParcelId: preferred.id } : {}),
    ...layout,
  };
}

function normalizeDegrees(value: number): number {
  if (!Number.isFinite(value)) return value;
  const normalized = value % 360;
  return normalized < 0 ? normalized + 360 : normalized;
}

export function localAxesForPoint(
  point: { lat: number; lng: number },
  reference: { lat: number; lng: number },
  rotationDeg: number,
): GenerationLayoutLocalPoint {
  const referenceLatRad = reference.lat * Math.PI / 180;
  const eastM = (point.lng - reference.lng) * Math.PI / 180
    * EARTH_RADIUS_M
    * Math.max(MIN_COS_LATITUDE, Math.abs(Math.cos(referenceLatRad)));
  const northM = (point.lat - reference.lat) * Math.PI / 180 * EARTH_RADIUS_M;
  const theta = normalizeDegrees(rotationDeg) * Math.PI / 180;
  return {
    xM: eastM * Math.cos(theta) + northM * Math.sin(theta),
    yM: -eastM * Math.sin(theta) + northM * Math.cos(theta),
  };
}

export function latLngForLocalAxes(
  point: GenerationLayoutLocalPoint,
  reference: { lat: number; lng: number },
  rotationDeg: number,
): { lat: number; lng: number } {
  const theta = normalizeDegrees(rotationDeg) * Math.PI / 180;
  const eastM = point.xM * Math.cos(theta) - point.yM * Math.sin(theta);
  const northM = point.xM * Math.sin(theta) + point.yM * Math.cos(theta);
  const referenceLatRad = reference.lat * Math.PI / 180;
  return {
    lat: reference.lat + northM / EARTH_RADIUS_M * 180 / Math.PI,
    lng: reference.lng + eastM
      / (EARTH_RADIUS_M * Math.max(MIN_COS_LATITUDE, Math.abs(Math.cos(referenceLatRad))))
      * 180 / Math.PI,
  };
}

/**
 * Localize exclusion-zone rings into the same rotated, envelope-centred metre
 * plane as the packer output. Derived on demand (solver, plan sheet, exhibit
 * SVG) rather than stored, so frozen snapshots stay lean.
 */
export function localizeLayoutExclusionRings(
  zones: ReadonlyArray<Pick<GenerationLayoutExclusionZone, 'polygons'>>,
  parcel: { centroid: { lat: number; lng: number } },
  envelope: Pick<GenerationLayoutEnvelope, 'center'>,
  rotationDeg: number,
): GenerationLayoutLocalPoint[][] {
  const localCenter = localAxesForPoint(envelope.center, parcel.centroid, rotationDeg);
  return zones.flatMap(zone => zone.polygons.map(ring => ring
    .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    .map(point => {
      const local = localAxesForPoint(point, parcel.centroid, rotationDeg);
      return { xM: local.xM - localCenter.xM, yM: local.yM - localCenter.yM };
    })));
}

/** Derive the parcel's oriented bounding envelope in local metres. */
export function generationLayoutEnvelopeForParcel(
  parcel: SiteBoundaryParcel,
  rotationDeg: number,
): GenerationLayoutEnvelope | null {
  const polygon = parcel.polygon.filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  if (polygon.length < 3 || !Number.isFinite(parcel.centroid.lat) || !Number.isFinite(parcel.centroid.lng)) {
    return null;
  }
  const local = polygon.map(point => localAxesForPoint(point, parcel.centroid, rotationDeg));
  const minX = Math.min(...local.map(point => point.xM));
  const maxX = Math.max(...local.map(point => point.xM));
  const minY = Math.min(...local.map(point => point.yM));
  const maxY = Math.max(...local.map(point => point.yM));
  const widthM = maxX - minX;
  const heightM = maxY - minY;
  if (!Number.isFinite(widthM) || !Number.isFinite(heightM) || widthM <= 0 || heightM <= 0) {
    return null;
  }
  const localCenter = { xM: (minX + maxX) / 2, yM: (minY + maxY) / 2 };
  return {
    center: latLngForLocalAxes(localCenter, parcel.centroid, rotationDeg),
    widthM,
    heightM,
    parcelPolygonM: local.map(point => ({
      xM: point.xM - localCenter.xM,
      yM: point.yM - localCenter.yM,
    })),
  };
}

function containedAreaAcres(
  rectangle: GenerationTestFitRectangle,
  parcelPolygonM: readonly GenerationLayoutLocalPoint[],
): number {
  const rectangleGeometry = [closedRingM(rectangle.polygonM)] as polygonClipping.Polygon;
  const parcelGeometry = [closedRingM(parcelPolygonM)] as polygonClipping.Polygon;
  const intersection = polygonClipping.intersection(
    rectangleGeometry,
    parcelGeometry,
  ) as polygonClipping.MultiPolygon;
  return multiPolygonAreaM2(intersection) / SQUARE_METRES_PER_ACRE;
}

function squaredDistance(left: GenerationLayoutLocalPoint, right: GenerationLayoutLocalPoint): number {
  const dx = left.xM - right.xM;
  const dy = left.yM - right.yM;
  return dx * dx + dy * dy;
}

function pointToSegmentDistanceSquared(
  point: GenerationLayoutLocalPoint,
  start: GenerationLayoutLocalPoint,
  end: GenerationLayoutLocalPoint,
): number {
  const segmentX = end.xM - start.xM;
  const segmentY = end.yM - start.yM;
  const segmentLengthSquared = segmentX * segmentX + segmentY * segmentY;
  if (segmentLengthSquared === 0) return squaredDistance(point, start);
  const projection = Math.max(0, Math.min(1, (
    (point.xM - start.xM) * segmentX
    + (point.yM - start.yM) * segmentY
  ) / segmentLengthSquared));
  return squaredDistance(point, {
    xM: start.xM + projection * segmentX,
    yM: start.yM + projection * segmentY,
  });
}

function crossProduct(
  start: GenerationLayoutLocalPoint,
  end: GenerationLayoutLocalPoint,
  point: GenerationLayoutLocalPoint,
): number {
  return (end.xM - start.xM) * (point.yM - start.yM)
    - (end.yM - start.yM) * (point.xM - start.xM);
}

function segmentIntersectionTolerance(
  aStart: GenerationLayoutLocalPoint,
  aEnd: GenerationLayoutLocalPoint,
  bStart: GenerationLayoutLocalPoint,
  bEnd: GenerationLayoutLocalPoint,
): number {
  const aLength = Math.hypot(aEnd.xM - aStart.xM, aEnd.yM - aStart.yM);
  const bLength = Math.hypot(bEnd.xM - bStart.xM, bEnd.yM - bStart.yM);
  return Math.max(1, aLength * bLength) * SEGMENT_INTERSECTION_RELATIVE_TOLERANCE;
}

function pointWithinSegmentBounds(
  point: GenerationLayoutLocalPoint,
  start: GenerationLayoutLocalPoint,
  end: GenerationLayoutLocalPoint,
): boolean {
  return point.xM >= Math.min(start.xM, end.xM) - CLEARANCE_TOLERANCE_M
    && point.xM <= Math.max(start.xM, end.xM) + CLEARANCE_TOLERANCE_M
    && point.yM >= Math.min(start.yM, end.yM) - CLEARANCE_TOLERANCE_M
    && point.yM <= Math.max(start.yM, end.yM) + CLEARANCE_TOLERANCE_M;
}

function segmentsIntersect(
  aStart: GenerationLayoutLocalPoint,
  aEnd: GenerationLayoutLocalPoint,
  bStart: GenerationLayoutLocalPoint,
  bEnd: GenerationLayoutLocalPoint,
): boolean {
  const tolerance = segmentIntersectionTolerance(aStart, aEnd, bStart, bEnd);
  const aToBStart = crossProduct(aStart, aEnd, bStart);
  const aToBEnd = crossProduct(aStart, aEnd, bEnd);
  const bToAStart = crossProduct(bStart, bEnd, aStart);
  const bToAEnd = crossProduct(bStart, bEnd, aEnd);
  const strictCrossing = (
    ((aToBStart > tolerance && aToBEnd < -tolerance)
      || (aToBStart < -tolerance && aToBEnd > tolerance))
    && ((bToAStart > tolerance && bToAEnd < -tolerance)
      || (bToAStart < -tolerance && bToAEnd > tolerance))
  );
  if (strictCrossing) return true;
  return (Math.abs(aToBStart) <= tolerance && pointWithinSegmentBounds(bStart, aStart, aEnd))
    || (Math.abs(aToBEnd) <= tolerance && pointWithinSegmentBounds(bEnd, aStart, aEnd))
    || (Math.abs(bToAStart) <= tolerance && pointWithinSegmentBounds(aStart, bStart, bEnd))
    || (Math.abs(bToAEnd) <= tolerance && pointWithinSegmentBounds(aEnd, bStart, bEnd));
}

function segmentDistanceSquared(
  aStart: GenerationLayoutLocalPoint,
  aEnd: GenerationLayoutLocalPoint,
  bStart: GenerationLayoutLocalPoint,
  bEnd: GenerationLayoutLocalPoint,
): number {
  if (segmentsIntersect(aStart, aEnd, bStart, bEnd)) return 0;
  return Math.min(
    pointToSegmentDistanceSquared(aStart, bStart, bEnd),
    pointToSegmentDistanceSquared(aEnd, bStart, bEnd),
    pointToSegmentDistanceSquared(bStart, aStart, aEnd),
    pointToSegmentDistanceSquared(bEnd, aStart, aEnd),
  );
}

function openRing(
  points: readonly GenerationLayoutLocalPoint[],
): readonly GenerationLayoutLocalPoint[] {
  if (points.length < 2) return points;
  const first = points[0]!;
  const last = points.at(-1)!;
  return first.xM === last.xM && first.yM === last.yM
    ? points.slice(0, -1)
    : points;
}

/** Exact shortest perimeter-to-perimeter distance in the shared local-metre plane. */
export function minimumPolygonBoundaryClearanceM(
  leftPolygon: readonly GenerationLayoutLocalPoint[],
  rightPolygon: readonly GenerationLayoutLocalPoint[],
): number | null {
  const left = openRing(leftPolygon);
  const right = openRing(rightPolygon);
  if (left.length < 2 || right.length < 2) return null;
  let minimumSquared = Number.POSITIVE_INFINITY;
  for (let leftIndex = 0; leftIndex < left.length; leftIndex += 1) {
    const leftStart = left[leftIndex]!;
    const leftEnd = left[(leftIndex + 1) % left.length]!;
    for (let rightIndex = 0; rightIndex < right.length; rightIndex += 1) {
      const rightStart = right[rightIndex]!;
      const rightEnd = right[(rightIndex + 1) % right.length]!;
      minimumSquared = Math.min(
        minimumSquared,
        segmentDistanceSquared(leftStart, leftEnd, rightStart, rightEnd),
      );
      if (minimumSquared === 0) return 0;
    }
  }
  return Number.isFinite(minimumSquared) ? Math.sqrt(Math.max(0, minimumSquared)) : null;
}

function assertCustomFinite(value: number, label: string, min: number, max: number): void {
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(
      `${label} must be finite and between ${min} and ${max}; received ${value}`,
    );
  }
}

function localCustomPath(
  path: ReadonlyArray<{ lat: number; lng: number }>,
  label: string,
  minPoints: number,
  anchor: { lat: number; lng: number },
  rotationDeg: number,
): GenerationLayoutLocalPoint[] {
  const finite = path.filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  if (finite.length < minPoints) {
    throw new RangeError(`${label} needs at least ${minPoints} finite vertices; received ${finite.length}.`);
  }
  return finite.map(point => localAxesForPoint(point, anchor, rotationDeg));
}

/**
 * Localize world-frame studio custom components into the shared rotated,
 * envelope-centred metre plane and express each as a component rectangle.
 * WORLD-frame geometry: unlike manual placements, customs do NOT rotate with
 * the plant grid — a rect's local bearing is `custom.rotationDeg −
 * layout.rotationDeg`. Road centerlines buffer into corridor polygons via a
 * deterministic per-segment oriented-rectangle union (fixed order, fixed
 * library); a self-splitting union yields one component per corridor polygon.
 * Everything is marked placement:'pinned' so the exclusion sweep never
 * translates it. Throws RangeError on invalid input (caller maps this to the
 * indeterminate result).
 */
function customComponentRectangles(
  customs: readonly GenerationCustomComponent[],
  anchor: { lat: number; lng: number },
  layoutRotationDeg: number,
  packingHalf: { halfWidthM: number; halfHeightM: number } | null,
): GenerationTestFitRectangle[] {
  const rectangles: GenerationTestFitRectangle[] = [];
  const seenIds = new Set<string>();
  const withinPackingEnvelope = (ring: readonly GenerationLayoutLocalPoint[]): boolean => (
    packingHalf === null
    || ring.every(point => (
      point.xM >= -packingHalf.halfWidthM - CLEARANCE_TOLERANCE_M
      && point.xM <= packingHalf.halfWidthM + CLEARANCE_TOLERANCE_M
      && point.yM >= -packingHalf.halfHeightM - CLEARANCE_TOLERANCE_M
      && point.yM <= packingHalf.halfHeightM + CLEARANCE_TOLERANCE_M
    ))
  );
  for (const custom of customs) {
    if (typeof custom.id !== 'string' || custom.id.length === 0) {
      throw new RangeError('Custom component ids must be non-empty strings.');
    }
    if (seenIds.has(custom.id)) {
      throw new RangeError(`Duplicate custom component id: ${custom.id}.`);
    }
    seenIds.add(custom.id);
    const label = typeof custom.label === 'string' && custom.label.length > 0
      ? custom.label
      : custom.id;

    if (custom.shape.type === 'rect') {
      assertCustomFinite(custom.shape.widthM, `Custom component ${custom.id} width`, MIN_CUSTOM_DIMENSION_M, MAX_CUSTOM_DIMENSION_M);
      assertCustomFinite(custom.shape.heightM, `Custom component ${custom.id} height`, MIN_CUSTOM_DIMENSION_M, MAX_CUSTOM_DIMENSION_M);
      if (!custom.position
        || !Number.isFinite(custom.position.lat)
        || !Number.isFinite(custom.position.lng)) {
        throw new RangeError(`Custom component ${custom.id} needs a finite position.`);
      }
      const worldRotationDeg = custom.rotationDeg ?? 0;
      assertCustomFinite(worldRotationDeg, `Custom component ${custom.id} rotation`, -MAX_CUSTOM_ROTATION_DEG, MAX_CUSTOM_ROTATION_DEG);
      const centre = localAxesForPoint(custom.position, anchor, layoutRotationDeg);
      const localRotationDeg = worldRotationDeg - layoutRotationDeg;
      const polygonM = rotatedClosedPolygon(
        centre.xM,
        centre.yM,
        custom.shape.widthM,
        custom.shape.heightM,
        localRotationDeg,
      );
      rectangles.push({
        id: `custom-${custom.id}`,
        label,
        kind: custom.kind,
        areaAcres: custom.shape.widthM * custom.shape.heightM / SQUARE_METRES_PER_ACRE,
        xM: centre.xM,
        yM: centre.yM,
        widthM: custom.shape.widthM,
        heightM: custom.shape.heightM,
        polygonM,
        fitsWithinEnvelope: withinPackingEnvelope(polygonM),
        dimensionBasis: 'user-defined',
        lifecycle: 'permanent',
        rotationDeg: localRotationDeg,
        placement: 'pinned',
      });
      continue;
    }

    if (custom.shape.type === 'polygon') {
      const ring = openRing(localCustomPath(
        custom.shape.path,
        `Custom component ${custom.id} polygon`,
        3,
        anchor,
        layoutRotationDeg,
      ));
      if (ring.length < 3) {
        throw new RangeError(`Custom component ${custom.id} polygon needs at least 3 distinct vertices.`);
      }
      const areaM2 = multiPolygonAreaM2([[closedRingM(ring)]] as polygonClipping.MultiPolygon);
      if (!(areaM2 > 0)) {
        throw new RangeError(`Custom component ${custom.id} polygon has no area.`);
      }
      const xs = ring.map(point => point.xM);
      const ys = ring.map(point => point.yM);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const polygonM = [...ring.map(point => ({ ...point })), { ...ring[0]! }];
      rectangles.push({
        id: `custom-${custom.id}`,
        label,
        kind: custom.kind,
        areaAcres: areaM2 / SQUARE_METRES_PER_ACRE,
        xM: (minX + maxX) / 2,
        yM: (minY + maxY) / 2,
        widthM: maxX - minX,
        heightM: maxY - minY,
        polygonM,
        fitsWithinEnvelope: withinPackingEnvelope(polygonM),
        dimensionBasis: 'user-defined',
        lifecycle: 'permanent',
        placement: 'pinned',
      });
      continue;
    }

    // Polyline road: buffer the centerline into a corridor polygon.
    assertCustomFinite(custom.shape.widthM, `Custom component ${custom.id} corridor width`, MIN_CUSTOM_DIMENSION_M, MAX_CUSTOM_DIMENSION_M);
    const path = localCustomPath(
      custom.shape.path,
      `Custom component ${custom.id} centerline`,
      2,
      anchor,
      layoutRotationDeg,
    );
    const halfWidthM = custom.shape.widthM / 2;
    let corridor: polygonClipping.MultiPolygon | null = null;
    for (let index = 0; index < path.length - 1; index += 1) {
      const start = path[index]!;
      const end = path[index + 1]!;
      const dxM = end.xM - start.xM;
      const dyM = end.yM - start.yM;
      const lengthM = Math.sqrt(dxM * dxM + dyM * dyM);
      if (lengthM <= 0) continue;
      const normalXM = -dyM / lengthM * halfWidthM;
      const normalYM = dxM / lengthM * halfWidthM;
      const segment = [[
        [start.xM + normalXM, start.yM + normalYM],
        [end.xM + normalXM, end.yM + normalYM],
        [end.xM - normalXM, end.yM - normalYM],
        [start.xM - normalXM, start.yM - normalYM],
        [start.xM + normalXM, start.yM + normalYM],
      ]] as polygonClipping.Polygon;
      corridor = corridor === null
        ? polygonClipping.union(segment)
        : polygonClipping.union(corridor, segment);
    }
    if (!corridor || corridor.length === 0) {
      throw new RangeError(`Custom component ${custom.id} centerline has no length.`);
    }
    corridor.forEach((polygon, polygonIndex) => {
      const outer = openRing(polygon[0]!.map(([xM, yM]) => ({ xM, yM })));
      if (outer.length < 3) return;
      const polygonM = [...outer, { ...outer[0]! }];
      const xs = outer.map(point => point.xM);
      const ys = outer.map(point => point.yM);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      rectangles.push({
        id: corridor.length > 1
          ? `custom-${custom.id}-${polygonIndex + 1}`
          : `custom-${custom.id}`,
        label: corridor.length > 1 ? `${label} (${polygonIndex + 1})` : label,
        kind: custom.kind,
        areaAcres: multiPolygonAreaM2([polygon]) / SQUARE_METRES_PER_ACRE,
        xM: (minX + maxX) / 2,
        yM: (minY + maxY) / 2,
        widthM: maxX - minX,
        heightM: maxY - minY,
        polygonM,
        fitsWithinEnvelope: withinPackingEnvelope(polygonM),
        dimensionBasis: 'user-defined',
        lifecycle: 'permanent',
        placement: 'pinned',
      });
    });
  }
  return rectangles;
}

function syntheticEnvelopeAcres(input: BuildGenerationParcelTestFitInput): number {
  const ccsActive = input.footprint.ccsAcres > 0
    || input.footprint.compressionAcres > 0;
  const physicalAcres = input.footprint.plantAcres
    + input.footprint.coolingAcres
    + input.footprint.switchyardAcres
    + input.footprint.ccsAcres
    + input.footprint.compressionAcres;
  const installed = input.installedReservations;
  const installedAcres = installed
    ? (
        installed.blockQuantity
          * installed.generationBlockEnvelope.lengthM
          * installed.generationBlockEnvelope.widthM
        + [
            installed.coolingEnvelope,
            installed.switchyardEnvelope,
            ccsActive ? installed.ccsCaptureEnvelope : undefined,
            ccsActive ? installed.ccsCompressionEnvelope : undefined,
            installed.naturalGasRouteEnvelope,
            ccsActive ? installed.co2ExportRouteEnvelope : undefined,
            installed.constructionLaydownEnvelope,
          ].reduce((sum, envelope) => (
            sum + (envelope ? envelope.lengthM * envelope.widthM : 0)
          ), 0)
      ) / SQUARE_METRES_PER_ACRE
    : 0;
  return Math.max(
    input.fallbackEnvelopeAcres ?? 0,
    Math.max(physicalAcres, installedAcres) * 1.25,
    1,
  );
}

/**
 * Build the same deterministic physical layout used by the UI and publisher.
 * A rectangular fit is necessary but not sufficient: the local rectangles are
 * also intersected with the selected parcel polygon so concave boundaries do
 * not produce a false whole-parcel fit.
 */
/**
 * Apply user-set component names over the solver's generated labels.
 *
 * Deliberately a PURE POST-PASS on the solved result: it rewrites `label` and
 * nothing else, so no rename can perturb geometry, area, containment or any
 * downstream physics. Because it runs inside the test-fit builder, one rename
 * reaches every consumer — canvas, layer tree, inspector, exports and reports —
 * without each of them needing to know the override exists.
 *
 * Blank / whitespace-only overrides fall back to the solver label, so clearing
 * the field restores the original name rather than blanking the component.
 */
function applyComponentLabelOverrides(
  components: GenerationLayoutComponentPolygon[],
  overrides: Record<string, string> | undefined,
): GenerationLayoutComponentPolygon[] {
  if (!overrides || Object.keys(overrides).length === 0) return components;
  return components.map(component => {
    const override = overrides[component.rectangle.id]?.trim();
    if (!override) return component;
    return {
      ...component,
      rectangle: { ...component.rectangle, label: override },
    };
  });
}

export function buildGenerationParcelTestFit(
  input: BuildGenerationParcelTestFitInput,
): GenerationParcelTestFitResult {
  const layout = normalizedLayout(input.layout, input.parcels);
  const requestedParcel = layout.targetParcelId
    ? input.parcels.find(parcel => parcel.id === layout.targetParcelId) ?? null
    : null;
  const envelope = requestedParcel
    ? generationLayoutEnvelopeForParcel(requestedParcel, layout.rotationDeg)
    : null;
  const anchor = envelope?.center ?? input.fallbackAnchor;

  // Exclusion zones are snapshotted on the layout; localize them into the same
  // rotated parcel-centred metre plane as the envelope so subtraction and
  // overlap checks share one geometry convention with the packer output.
  const exclusionSet = layout.exclusions;
  const exclusionZones = exclusionSet?.zones ?? [];
  let exclusionGeometry: LocalExclusionGeometry | null = null;
  let exclusionAnalysisFailed = false;
  if (envelope && requestedParcel && exclusionZones.length > 0) {
    const exclusionRings = localizeLayoutExclusionRings(
      exclusionZones,
      requestedParcel,
      envelope,
      layout.rotationDeg,
    );
    exclusionGeometry = localExclusionGeometry(exclusionRings, envelope.parcelPolygonM);
    exclusionAnalysisFailed = exclusionGeometry === null;
  }
  const syntheticSideM = Math.sqrt(syntheticEnvelopeAcres(input) * SQUARE_METRES_PER_ACRE);
  const usableEnvelope = envelope
    ? { widthM: envelope.widthM, heightM: envelope.heightM }
    : { widthM: syntheticSideM, heightM: syntheticSideM };

  let plan: GenerationTestFitResult;
  let customRectangles: GenerationTestFitRectangle[];
  try {
    plan = buildGenerationTestFit({
      usableEnvelope,
      generationBlockCount: input.generationBlockCount,
      footprint: input.footprint,
      edgeSetbackM: layout.edgeSetbackM,
      componentGapM: layout.componentGapM,
      orientation: layout.orientation,
      aspectRatios: { generationBlock: layout.generationBlockAspectRatio },
      installedReservations: input.installedReservations,
      ...(layout.rowRoadWidthM != null ? { rowRoadWidthM: layout.rowRoadWidthM } : {}),
      ...(layout.packingScheme != null ? { packingScheme: layout.packingScheme } : {}),
      ...(layout.schemaVersion != null ? { schemaVersion: layout.schemaVersion } : {}),
      ...(layout.manualPlacements != null && layout.manualPlacements.length > 0
        ? { manualPlacements: layout.manualPlacements }
        : {}),
    });
    if ((layout.customComponents?.length ?? 0) > 0 && layout.schemaVersion !== 2) {
      throw new RangeError('Custom components require layout schemaVersion 2.');
    }
    customRectangles = layout.schemaVersion === 2 && layout.customComponents?.length
      ? customComponentRectangles(
          layout.customComponents,
          anchor,
          layout.rotationDeg,
          envelope
            ? {
                halfWidthM: (envelope.widthM - 2 * layout.edgeSetbackM) / 2,
                halfHeightM: (envelope.heightM - 2 * layout.edgeSetbackM) / 2,
              }
            : null,
        )
      : [];
  } catch (error) {
    return {
      status: 'indeterminate',
      plan: null,
      layout,
      parcel: requestedParcel,
      parcels: input.parcels,
      envelope,
      anchor,
      components: [],
      containedAcres: null,
      outsideAcres: null,
      containmentPct: null,
      minimumParcelEdgeClearanceM: null,
      parcelEdgeSetbackSatisfied: null,
      error: (error as Error).message,
      caveats: ['Correct the layout inputs before relying on or publishing this planning geometry.'],
    };
  }

  // Fixed studio geometry (pinned plan rectangles + custom components) never
  // translates; the exclusion-aware sweep moves only the FREE packed group,
  // and fixed geometry joins the obstacle set it steers around.
  const pinnedPlanRectangles = plan.rectangles
    .filter(rectangle => rectangle.placement === 'pinned');
  const freePlanRectangles = pinnedPlanRectangles.length > 0
    ? plan.rectangles.filter(rectangle => rectangle.placement !== 'pinned')
    : plan.rectangles;
  const fixedRectangles = [...pinnedPlanRectangles, ...customRectangles];
  const studioObstaclesActive = layout.schemaVersion === 2 && fixedRectangles.length > 0;
  let obstacleAnalysisFailed = false;
  let sweepObstacles: polygonClipping.MultiPolygon = exclusionGeometry?.clippedUnion ?? [];
  let fixedUnion: polygonClipping.MultiPolygon = [];
  const fixedOthersUnionById = new Map<string, polygonClipping.MultiPolygon | null>();
  if (studioObstaclesActive) {
    try {
      for (const rectangle of fixedRectangles) {
        const geometry = [closedRingM(rectangle.polygonM)] as polygonClipping.Polygon;
        fixedUnion = fixedUnion.length === 0
          ? polygonClipping.union(geometry)
          : polygonClipping.union(fixedUnion, geometry);
        sweepObstacles = sweepObstacles.length === 0
          ? polygonClipping.union(geometry)
          : polygonClipping.union(sweepObstacles, geometry);
      }
      for (const rectangle of fixedRectangles) {
        let others: polygonClipping.MultiPolygon = [];
        for (const other of fixedRectangles) {
          if (other.id === rectangle.id) continue;
          const geometry = [closedRingM(other.polygonM)] as polygonClipping.Polygon;
          others = others.length === 0
            ? polygonClipping.union(geometry)
            : polygonClipping.union(others, geometry);
        }
        fixedOthersUnionById.set(rectangle.id, others);
      }
    } catch {
      obstacleAnalysisFailed = true;
      sweepObstacles = exclusionGeometry?.clippedUnion ?? [];
      fixedUnion = [];
      fixedOthersUnionById.clear();
    }
  }

  // With exclusions (or fixed studio obstacles), sweep the packed group away
  // from the default centred anchor when that reduces overlap. The sweep is
  // skipped for overflowing plans: translation cannot fix an envelope that is
  // too small.
  const placementOffset: PlacementOffsetM = envelope
    && sweepObstacles.length > 0
    && freePlanRectangles.length > 0
    && plan.status === 'fit'
    ? searchPlacementOffset({
        rectangles: freePlanRectangles,
        parcelPolygonM: envelope.parcelPolygonM,
        exclusionGeometry: sweepObstacles,
        usableEnvelope: { widthM: envelope.widthM, heightM: envelope.heightM },
        requiredEnvelope: plan.freeRequiredEnvelope ?? {
          widthM: plan.requiredEnvelope.widthM,
          heightM: plan.requiredEnvelope.heightM,
        },
      }).offset
    : { dxM: 0, dyM: 0 };

  const exclusionsActive = exclusionZones.length > 0;
  const allRectangles = customRectangles.length > 0
    ? [...plan.rectangles, ...customRectangles]
    : plan.rectangles;
  const components = allRectangles.map((planRectangle): GenerationLayoutComponentPolygon => {
    const rectangle = planRectangle.placement === 'pinned'
      ? planRectangle
      : translateRectangle(planRectangle, placementOffset);
    const containedAcres = envelope
      ? Math.min(rectangle.areaAcres, containedAreaAcres(rectangle, envelope.parcelPolygonM))
      : null;
    const parcelEdgeClearanceM = envelope
      ? minimumPolygonBoundaryClearanceM(rectangle.polygonM, envelope.parcelPolygonM)
      : null;
    const exclusionOverlapAcres = exclusionGeometry
      ? polygonOverlapAcres(rectangle.polygonM, exclusionGeometry.clippedUnion)
      : exclusionAnalysisFailed ? null : undefined;
    const obstacleOverlapAcres = !studioObstaclesActive
      ? undefined
      : obstacleAnalysisFailed
        ? null
        : planRectangle.placement === 'pinned'
          ? polygonOverlapAcres(
              rectangle.polygonM,
              fixedOthersUnionById.get(planRectangle.id) ?? [],
            )
          : polygonOverlapAcres(rectangle.polygonM, fixedUnion);
    return {
      rectangle,
      coordinates: rectangle.polygonM.map(point => latLngForLocalAxes(
        point,
        anchor,
        layout.rotationDeg,
      )),
      containedAcres,
      outsideAcres: containedAcres == null ? null : Math.max(0, rectangle.areaAcres - containedAcres),
      parcelEdgeClearanceM,
      meetsParcelEdgeSetback: parcelEdgeClearanceM == null
        ? null
        : parcelEdgeClearanceM + CLEARANCE_TOLERANCE_M >= layout.edgeSetbackM,
      ...(exclusionsActive ? { exclusionOverlapAcres: exclusionOverlapAcres ?? null } : {}),
      ...(studioObstaclesActive ? { obstacleOverlapAcres: obstacleOverlapAcres ?? null } : {}),
    };
  });
  // Custom components consume ground alongside the plan's own rectangles, so
  // the containment denominators include them. Legacy inputs have none, so
  // the legacy value is bit-identical (x + 0 === x).
  const totalPlannedAreaAcres = plan.totalComponentAreaAcres
    + customRectangles.reduce((sum, rectangle) => sum + rectangle.areaAcres, 0);
  const containedAcres = envelope
    ? components.reduce((sum, component) => sum + (component.containedAcres ?? 0), 0)
    : null;
  const outsideAcres = containedAcres == null
    ? null
    : Math.max(0, totalPlannedAreaAcres - containedAcres);
  const containmentPct = containedAcres == null || totalPlannedAreaAcres <= 0
    ? null
    : containedAcres / totalPlannedAreaAcres * 100;
  const componentClearances = components
    .map(component => component.parcelEdgeClearanceM)
    .filter((clearance): clearance is number => clearance != null);
  const minimumParcelEdgeClearanceM = envelope && componentClearances.length === components.length
    ? Math.min(...componentClearances)
    : null;
  const parcelEdgeSetbackSatisfied = minimumParcelEdgeClearanceM == null
    ? null
    : minimumParcelEdgeClearanceM + CLEARANCE_TOLERANCE_M >= layout.edgeSetbackM;
  const parcelResolved = requestedParcel != null && envelope != null;

  const componentOverlaps = exclusionsActive
    ? components.map(component => component.exclusionOverlapAcres)
    : [];
  const totalExclusionOverlapAcres = !exclusionsActive
    ? undefined
    : componentOverlaps.some(overlap => overlap == null)
      ? null
      : componentOverlaps.reduce<number>((sum, overlap) => sum + (overlap ?? 0), 0);
  // No zones applied -> gate passes; zones applied -> overlap must be computed
  // and negligible. A failed analysis (null) can never report `fits`.
  const exclusionGateSatisfied = !exclusionsActive
    || (totalExclusionOverlapAcres != null
      && totalExclusionOverlapAcres <= CONTAINMENT_TOLERANCE_ACRES);

  // Studio fixed geometry: manual collisions are reported, never resolved,
  // and a collision (or failed analysis) blocks the `fits` verdict.
  const componentObstacleOverlaps = studioObstaclesActive
    ? components.map(component => component.obstacleOverlapAcres)
    : [];
  const totalObstacleOverlapAcres = !studioObstaclesActive
    ? undefined
    : componentObstacleOverlaps.some(overlap => overlap == null)
      ? null
      : componentObstacleOverlaps.reduce<number>((sum, overlap) => sum + (overlap ?? 0), 0);
  const obstacleGateSatisfied = !studioObstaclesActive
    || (totalObstacleOverlapAcres != null
      && totalObstacleOverlapAcres <= CONTAINMENT_TOLERANCE_ACRES);

  const fits = parcelResolved
    && plan.status === 'fit'
    && outsideAcres != null
    && outsideAcres <= CONTAINMENT_TOLERANCE_ACRES
    && parcelEdgeSetbackSatisfied === true
    && exclusionGateSatisfied
    && exclusionSet?.femaStatus !== 'partial'
    && exclusionSet?.femaStatus !== 'unavailable'
    && (!exclusionSet?.nwiStatus || exclusionSet.nwiStatus === 'applied')
    && obstacleGateSatisfied;
  const staleParcelId = layout.targetParcelId != null && requestedParcel == null;

  const exclusionCaveats: string[] = [];
  if (exclusionSet?.femaStatus === 'unavailable') {
    exclusionCaveats.push(
      `FEMA flood exclusions unavailable${exclusionSet.femaUnavailableReason ? ` (${exclusionSet.femaUnavailableReason})` : ''}; the buildable area is not flood-screened.`,
    );
  }
  if (exclusionSet?.femaStatus === 'partial') {
    exclusionCaveats.push(`FEMA flood screening incomplete (${exclusionSet.femaUnavailableReason ?? 'mapping coverage unresolved'}); known exclusion polygons are retained, but remaining acreage is not confirmed flood-free.`);
  }
  if (exclusionSet?.nwiStatus && exclusionSet.nwiStatus !== 'applied') {
    exclusionCaveats.push(`NWI wetlands screening incomplete (${exclusionSet.nwiUnavailableReason ?? 'mapping coverage unresolved'}); remaining land is not confirmed wetlands-free.`);
  }
  if (exclusionsActive && exclusionAnalysisFailed) {
    exclusionCaveats.push(
      'Exclusion-zone geometry could not be applied to the parcel; the layout cannot be verified against the recorded no-build areas.',
    );
  } else if (exclusionsActive && exclusionGeometry) {
    exclusionCaveats.push(
      'Recorded no-build exclusion zones are subtracted from the buildable area; zone geometry reflects its source at retrieval time and is screening-grade, not a regulatory determination.',
    );
  }

  return {
    status: !parcelResolved ? 'indeterminate' : fits ? 'fits' : 'partial',
    plan,
    layout,
    parcel: requestedParcel,
    parcels: input.parcels,
    envelope,
    anchor,
    components: applyComponentLabelOverrides(components, layout.componentLabels),
    containedAcres,
    outsideAcres,
    containmentPct,
    minimumParcelEdgeClearanceM,
    parcelEdgeSetbackSatisfied,
    ...(exclusionGeometry
      ? {
          usable: {
            parcelAcres: exclusionGeometry.parcelAcres,
            excludedAcres: exclusionGeometry.excludedAcres,
            usableAcres: exclusionGeometry.usableAcres,
            femaStatus: exclusionSet?.femaStatus ?? 'not-fetched',
          },
        }
      : {}),
    ...(totalExclusionOverlapAcres !== undefined
      ? { exclusionOverlapAcres: totalExclusionOverlapAcres }
      : {}),
    ...(totalObstacleOverlapAcres !== undefined
      ? { obstacleOverlapAcres: totalObstacleOverlapAcres }
      : {}),
    ...((exclusionsActive || studioObstaclesActive)
      && (placementOffset.dxM !== 0 || placementOffset.dyM !== 0)
      ? { placementOffsetM: placementOffset }
      : {}),
    error: staleParcelId
      ? `Layout parcel ${layout.targetParcelId} is no longer present on the site.`
      : requestedParcel && !envelope
        ? 'The selected parcel does not contain a usable polygon.'
        : null,
    caveats: [
      ...plan.caveats,
      parcelResolved
        ? 'Parcel containment and the entered edge setback are checked against the selected boundary; easements, grading, drainage and equipment-specific clearances remain external.'
        : 'No usable parcel boundary is selected; the displayed envelope is synthetic and cannot produce a parcel-fit verdict.',
      ...exclusionCaveats,
      ...(customRectangles.length > 0
        ? ['User-defined components are screening geometry at entered dimensions; they are held to the same containment and edge-setback checks as packed equipment but carry no vendor basis.']
        : []),
      ...(studioObstaclesActive && totalObstacleOverlapAcres == null
        ? ['Overlap between pinned/custom components could not be computed; manual-placement collisions cannot be verified.']
        : []),
      ...(studioObstaclesActive
        && totalObstacleOverlapAcres != null
        && totalObstacleOverlapAcres > CONTAINMENT_TOLERANCE_ACRES
        ? [`Pinned or user-defined components overlap other components by ${totalObstacleOverlapAcres.toFixed(2)} acres; the packer reports but does not resolve manual-placement collisions.`]
        : []),
    ],
  };
}
